/**
 * The Ask Dink panel (DNKADM-0434 U2, redesigned in DNKADM-0460): a right-side drawer
 * on desktop and iPad, a full-screen sheet on a phone (under 768 px), where an owner
 * or staff member asks how to do something and gets a short answer plus up to three
 * buttons to the right screens.
 *
 *  - A persistent (non-modal) drawer, so the page behind stays usable: a link button
 *    navigates in-app with the router and the drawer stays open on the new screen. The
 *    phone sheet covers the screen, so it closes on a link instead.
 *  - Under the header, a strip names the current screen and the access the answers
 *    match (`Staff` or `Owner`, from the launcher).
 *  - The empty panel greets the operator and offers the starters for this screen
 *    (`askDinkStarters`), only those about screens in this session's route list. With
 *    none left, the starter section is not shown.
 *  - While an answer is on its way: three paced steps and the elapsed seconds. The
 *    question box stays usable, and Stop aborts the request (the fetch is cancelled).
 *  - Each answer shows where it came from, its follow-up suggestions (click = ask) and
 *    thumbs up/down → the feedback endpoint (see `AskDinkAnswer`).
 *  - The current conversation's last 6 turns (12 messages) go back as `history`; "New
 *    chat" starts a fresh one and keeps this session's question list. This session's
 *    questions are listed newest first in the history view; opening one makes it the
 *    current conversation (and cancels an answer still on its way). All of it lives in
 *    this component's state only: never localStorage/sessionStorage. The launcher
 *    remounts the panel when the signed-in operator changes, so sign-out clears it, and
 *    an unmount aborts a request in flight.
 *  - Header icons, left to right (addendum item 11): New chat, History, Expand (the
 *    drawer widens to 720 px and back; not on the phone sheet, which is already full
 *    screen), Close. The expanded width lasts for the session, across close and reopen.
 *  - Copy on each answer puts the scrubbed answer text on the clipboard and toasts.
 *  - The composer is one rounded row: the question box, the `composerExtras` slot (empty
 *    in this unit; DNKADM-0462 mic and DNKADM-0463 "Ask about this" fill it), then Send,
 *    which Stop replaces while an answer is on its way.
 *  - `429` and `503` show fixed copy; the disabled state (`404 ASSISTANT_DISABLED`) is
 *    reported to the launcher through `onDisabled`, which hides everything.
 */
import {
  Alert,
  Box,
  Button,
  ButtonBase,
  CircularProgress,
  Drawer,
  InputBase,
  Stack,
  Typography,
} from '@mui/material';
import {
  useEffect,
  useRef,
  useState,
  type FormEvent,
  type KeyboardEvent,
  type ReactNode,
} from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ASSISTANT_HISTORY_MESSAGES,
  ASSISTANT_HISTORY_TURNS,
  ASSISTANT_QUESTION_MAX,
  askAssistant,
  classifyAssistantError,
  sendAssistantFeedback,
  type AssistantAnswerKind,
  type AssistantTurn,
} from '@/api/assistant';
import { IconAction } from '@/components/IconAction';
import { useToast } from '@/components/useToast';
import { Icon, MONO } from '@/theme';
import { scrubBrand } from '@/utils/whitelabel';
import {
  AskDinkAnswer,
  QuestionBubble,
  type AskDinkAccessRole,
  type AskDinkExchange,
} from './AskDinkAnswer';
import {
  DINK_CAN,
  DINK_CANT,
  FOOTER_COPY,
  HISTORY_PRIVACY_COPY,
  LOADING_STEPS,
  LOADING_STEP_STARTS,
  RATE_LIMITED_COPY,
  UNAVAILABLE_COPY,
  UNAVAILABLE_DETAIL_COPY,
  VOICE_BLOCKED_COPY,
  VOICE_BUTTON_LABEL,
  PICK_BUTTON_LABEL,
  PICK_CHIP_REMOVE_LABEL,
  PICK_MODE_PLACEHOLDER,
  VOICE_LISTENING_PLACEHOLDER,
} from './askDinkCopy';
import { ASK_DINK_PANEL_ATTR, pickQuestion, type AskDinkPick } from './askDinkPick';
import type { AskDinkScreen } from './askDinkScreen';
import { ASK_DINK_STARTERS, DEFAULT_STARTER_SCREEN, visibleStarters } from './askDinkStarters';
import type { AskDinkRoute } from './routeCatalogue';
import { useAskAboutThis } from './useAskAboutThis';
import { useVoiceDictation } from './useVoiceDictation';
import { useCalendarZone } from '@/location/useCalendarZone';
import { formatInstantMs, TIME_ONLY } from '@/utils/instantFormat';

const PANEL_WIDTH = 440;
/** The drawer's width when expanded (addendum item 11). */
const EXPANDED_WIDTH = 720;
/** Question/answer pairs in the current conversation: one pair is one turn of history. */
const MAX_THREAD = ASSISTANT_HISTORY_TURNS;
/** This session's questions kept for the history view (memory only). */
const MAX_SESSION_LOG = 50;

/**
 * The wall clock, for timestamps taken in event handlers (ask, answer, failure). The
 * purity lint cannot tell `ask` is never called during render, so the read lives here.
 */
function clockNow(): number {
  return Date.now();
}

interface Pending {
  question: string;
  startedAt: number;
}

interface Failure {
  kind: 'rateLimited' | 'unavailable';
  question: string;
  at: number;
}

export interface AskDinkPanelProps {
  open: boolean;
  onClose: () => void;
  routes: AskDinkRoute[];
  onDisabled: () => void;
  /** The screen the operator is on (role strip + starters). */
  screen: AskDinkScreen;
  /** The access the answers match. */
  role: AskDinkAccessRole;
  /** For the greeting; null greets without a name. */
  firstName: string | null;
  /** The selected location, for "Help for …"; null leaves the line out. */
  locationName: string | null;
  /** Under 768 px: a full-screen sheet instead of the right drawer. */
  isPhone: boolean;
  /**
   * Controls between the question box and Send (DNKADM-0463 "Ask about this"). The mic
   * (DNKADM-0462) is built into the panel and sits before this slot.
   */
  composerExtras?: ReactNode;
}

/** The question box's accessible name (the rounded composer has no visible label). */
const COMPOSER_LABEL = 'Your question';

/** History row icon per answer kind; `other` is neutral, never the help-guide book. */
const HISTORY_ICON: Record<AssistantAnswerKind, { name: string; color: string }> = {
  help: { name: 'menu_book', color: 'info.main' },
  unsure: { name: 'help', color: 'warning.main' },
  other: { name: 'chat_bubble_outline', color: 'text.secondary' },
};

const COPIED_COPY = 'Copied';
const COPY_FAILED_COPY = "Couldn't copy the answer. Check clipboard permissions and try again.";

/** `m:ss`. */
function formatElapsed(seconds: number): string {
  return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;
}

/** Which loading step is current after `seconds`. */
function loadingStep(seconds: number): number {
  return LOADING_STEP_STARTS.filter((start) => seconds >= start).length;
}

function LoadingProgress({ elapsed }: { elapsed: number }) {
  const current = loadingStep(elapsed);
  return (
    <Stack
      role="status"
      sx={{
        gap: 1.25,
        p: 2,
        border: 1,
        borderColor: 'divider',
        borderRadius: 3,
        bgcolor: 'action.hover',
      }}
    >
      <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'baseline' }}>
        <Typography sx={{ fontSize: 14, fontWeight: 700 }}>Working on your answer</Typography>
        {/* Hidden from screen readers: a live region re-announcing every second is noise;
        the steps carry the progress. */}
        <Typography aria-hidden sx={{ fontFamily: MONO, fontSize: 12, color: 'text.secondary' }}>
          {formatElapsed(elapsed)}
        </Typography>
      </Stack>
      <Stack component="ol" sx={{ gap: 1, m: 0, p: 0, listStyle: 'none' }}>
        {LOADING_STEPS.map((step, index) => {
          const done = index < current;
          const active = index === current;
          return (
            <Stack
              component="li"
              key={step}
              direction="row"
              aria-current={active ? 'step' : undefined}
              sx={{
                alignItems: 'center',
                gap: 1.25,
                fontSize: 13.5,
                fontWeight: active ? 600 : 400,
                color: done || active ? 'text.primary' : 'text.secondary',
              }}
            >
              <Box sx={{ width: 16, display: 'flex', justifyContent: 'center', flex: 'none' }}>
                {done ? (
                  <Icon name="check" sx={{ fontSize: 16, color: 'success.main' }} />
                ) : active ? (
                  <CircularProgress size={12} aria-hidden />
                ) : (
                  <Icon name="radio_button_unchecked" sx={{ fontSize: 14 }} />
                )}
              </Box>
              {step}
            </Stack>
          );
        })}
      </Stack>
    </Stack>
  );
}

function UnavailableCard({ at, onRetry }: { at: number; onRetry: () => void }) {
  const calendarZone = useCalendarZone();
  return (
    <Stack sx={{ gap: 1.5 }}>
      <Stack
        role="alert"
        sx={{
          alignItems: 'center',
          textAlign: 'center',
          gap: 1.25,
          px: 2.5,
          py: 3,
          border: 1,
          borderColor: 'divider',
          borderRadius: 4,
          bgcolor: 'action.hover',
        }}
      >
        <Icon name="cloud_off" sx={{ fontSize: 32, color: 'warning.main' }} />
        <Typography component="h3" sx={{ fontSize: 17, fontWeight: 700 }}>
          {UNAVAILABLE_COPY}
        </Typography>
        <Typography sx={{ fontSize: 14, color: 'text.secondary', maxWidth: 320 }}>
          {UNAVAILABLE_DETAIL_COPY}
        </Typography>
        <Button
          variant="contained"
          startIcon={<Icon name="refresh" sx={{ fontSize: 18 }} />}
          onClick={onRetry}
        >
          Try again
        </Button>
        <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
          Last tried at{' '}
          <Box component="span" sx={{ fontFamily: MONO }}>
            {formatInstantMs(at, calendarZone, TIME_ONLY)}
          </Box>
        </Typography>
      </Stack>
      <Typography
        sx={{
          fontSize: 13,
          p: 1.5,
          border: 1,
          borderStyle: 'dashed',
          borderColor: 'divider',
          borderRadius: 3,
        }}
      >
        <b>Working with a member right now?</b>{' '}
        <Box component="span" sx={{ color: 'text.secondary' }}>
          Everything else in Dink still works. Only this help panel is down.
        </Box>
      </Typography>
    </Stack>
  );
}

export function AskDinkPanel({
  open,
  onClose,
  routes,
  onDisabled,
  screen,
  role,
  firstName,
  locationName,
  isPhone,
  composerExtras,
}: AskDinkPanelProps) {
  const calendarZone = useCalendarZone();
  const navigate = useNavigate();
  const showToast = useToast();
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const [draft, setDraft] = useState('');
  // Every answer this session, oldest first (shown newest first in the history view).
  const [log, setLog] = useState<AskDinkExchange[]>([]);
  // The current conversation: keys into `log`, oldest first.
  const [thread, setThread] = useState<number[]>([]);
  const [pending, setPending] = useState<Pending | null>(null);
  // Set by ask() before anything reads it.
  const [now, setNow] = useState(0);
  const [failure, setFailure] = useState<Failure | null>(null);
  const [view, setView] = useState<'chat' | 'history'>('chat');
  // Lasts for the session (memory only), across close and reopen.
  const [expanded, setExpanded] = useState(false);
  // Each ask/stop/clear bumps the sequence; a reply to an earlier one never lands later.
  const seqRef = useRef(0);
  const abortRef = useRef<AbortController | null>(null);
  // Dictation fills the box and never sends (DNKADM-0462).
  const voice = useVoiceDictation(
    () => draft,
    (text) => setDraft(text.slice(0, ASSISTANT_QUESTION_MAX)),
  );
  const { stop: stopVoice } = voice;
  // "Ask about this" (DNKADM-0463): the pick fills the box and the chip; it never sends.
  const [picked, setPicked] = useState<AskDinkPick | null>(null);
  const askAbout = useAskAboutThis(open && !isPhone, (pick) => {
    setPicked(pick);
    setDraft(pickQuestion(pick, screen.name).slice(0, ASSISTANT_QUESTION_MAX));
    inputRef.current?.focus();
  });
  const { cancel: cancelPick } = askAbout;
  const allowedPaths = new Set(routes.map((route) => route.path));
  const threadExchanges = thread.flatMap((key) => log.filter((e) => e.key === key));

  // The elapsed-seconds clock is a timer subscription, the one job an effect is for.
  useEffect(() => {
    if (pending === null) return;
    const id = window.setInterval(() => setNow(clockNow()), 1000);
    return () => window.clearInterval(id);
  }, [pending]);

  // The drawer is persistent (it stays mounted when closed), so a closed panel ends dictation.
  useEffect(() => {
    if (!open) {
      stopVoice();
      cancelPick();
    }
  }, [open, stopVoice, cancelPick]);

  // A request still on its way when the panel unmounts (sign-out remounts it) is cancelled.
  useEffect(() => () => abortRef.current?.abort(), []);

  async function ask(question: string) {
    const trimmed = question.trim();
    if (trimmed === '' || pending !== null) return;
    voice.cancel();
    setPicked(null);
    const seq = seqRef.current + 1;
    seqRef.current = seq;
    const controller = new AbortController();
    abortRef.current = controller;
    const history: AssistantTurn[] = threadExchanges
      .flatMap((e): AssistantTurn[] => [
        { role: 'user', text: e.question },
        { role: 'assistant', text: e.answer },
      ])
      .slice(-ASSISTANT_HISTORY_MESSAGES);
    const startedAt = clockNow();
    setPending({ question: trimmed, startedAt });
    setNow(startedAt);
    setFailure(null);
    setDraft('');
    setView('chat');
    try {
      const res = await askAssistant(
        { question: trimmed, routes, ...(history.length > 0 ? { history } : {}) },
        { signal: controller.signal },
      );
      if (seqRef.current !== seq) return;
      setLog((prev) =>
        [
          ...prev,
          { key: seq, question: trimmed, ...res, feedback: null, answeredAt: clockNow() },
        ].slice(-MAX_SESSION_LOG),
      );
      setThread((prev) => [...prev, seq].slice(-MAX_THREAD));
    } catch (err) {
      if (seqRef.current !== seq) return;
      const kind = classifyAssistantError(err);
      if (kind === 'disabled') {
        onDisabled();
        return;
      }
      setFailure({ kind, question: trimmed, at: clockNow() });
      // Give the question back so it can be sent again.
      setDraft(trimmed);
    } finally {
      if (seqRef.current === seq) {
        setPending(null);
        abortRef.current = null;
      }
    }
  }

  /** Cancel the request on its way and drop its answer. */
  function cancelPending() {
    abortRef.current?.abort();
    abortRef.current = null;
    seqRef.current += 1;
    setPending(null);
  }

  function handleStop() {
    const question = pending?.question ?? '';
    cancelPending();
    // Hand the question back unless something new is already being typed.
    setDraft((prev) => (prev.trim() === '' ? question : prev));
    inputRef.current?.focus();
  }

  function handleSubmit(event: FormEvent) {
    event.preventDefault();
    void ask(draft);
  }

  function handleInputKeyDown(event: KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      void ask(draft);
    }
  }

  function handlePanelKeyDown(event: KeyboardEvent<HTMLElement>) {
    if (event.key !== 'Escape') return;
    // Esc cancels picking first (the document listener in useAskAboutThis), then ends
    // dictation; the panel stays open for both.
    if (voice.listening) voice.stop();
    else onClose();
  }

  function handleClose() {
    voice.stop();
    onClose();
  }

  /** New chat: a fresh conversation. This session's question list is kept. */
  function handleNewChat() {
    voice.cancel();
    cancelPick();
    setPicked(null);
    cancelPending();
    setThread([]);
    setFailure(null);
    setDraft('');
    setView('chat');
    inputRef.current?.focus();
  }

  function handleOpenPast(key: number) {
    // An answer still on its way belongs to the conversation being left.
    cancelPending();
    setThread([key]);
    setFailure(null);
    setView('chat');
  }

  function handleNavigate(path: string) {
    void navigate(path);
    // The phone sheet covers the screen it just opened.
    if (isPhone) handleClose();
  }

  async function handleCopy(exchange: AskDinkExchange) {
    const clipboard = navigator.clipboard;
    if (clipboard === undefined || typeof clipboard.writeText !== 'function') {
      showToast(COPY_FAILED_COPY, 'error');
      return;
    }
    try {
      await clipboard.writeText(scrubBrand(exchange.answer) ?? exchange.answer);
    } catch {
      showToast(COPY_FAILED_COPY, 'error');
      return;
    }
    showToast(COPIED_COPY);
  }

  function setFeedback(key: number, value: boolean | null) {
    setLog((prev) => prev.map((e) => (e.key === key ? { ...e, feedback: value } : e)));
  }

  async function handleFeedback(exchange: AskDinkExchange, helpful: boolean) {
    setFeedback(exchange.key, helpful);
    try {
      await sendAssistantFeedback(exchange.questionId, helpful);
    } catch {
      // Not recorded — offer the choice again rather than claim it was saved.
      setFeedback(exchange.key, null);
    }
  }

  const empty = threadExchanges.length === 0 && pending === null && failure === null;
  // Only starters about screens this session can open (the same list the API is sent).
  const starters = visibleStarters(
    ASK_DINK_STARTERS[screen.starters ?? DEFAULT_STARTER_SCREEN],
    allowedPaths,
  );
  const startersLabel =
    screen.starters !== null && screen.name !== null
      ? `Common on ${screen.name}`
      : 'Common questions';
  const elapsed = pending === null ? 0 : Math.max(0, Math.floor((now - pending.startedAt) / 1000));
  const inHistory = view === 'history';
  const canClear = threadExchanges.length > 0 || pending !== null || failure !== null;
  const drawerWidth = expanded ? EXPANDED_WIDTH : PANEL_WIDTH;

  return (
    <Drawer
      anchor={isPhone ? 'bottom' : 'right'}
      variant="persistent"
      open={open}
      slotProps={{
        paper: {
          component: 'aside',
          ...{ [ASK_DINK_PANEL_ATTR]: '' },
          'aria-labelledby': 'ask-dink-title',
          onKeyDown: handlePanelKeyDown,
          sx: {
            width: isPhone ? '100%' : drawerWidth,
            maxWidth: '100vw',
            height: isPhone ? '100dvh' : undefined,
            display: 'flex',
            flexDirection: 'column',
            bgcolor: 'background.paper',
          },
        },
        transition: { onEntered: () => inputRef.current?.focus() },
      }}
    >
      <Stack
        direction="row"
        sx={{
          alignItems: 'center',
          gap: 1,
          pl: 2,
          pr: 1,
          py: 1.25,
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <Icon name="support_agent" sx={{ fontSize: 24, color: 'primary.main' }} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography id="ask-dink-title" component="h2" sx={{ fontSize: 17, fontWeight: 700 }}>
            Ask Dink
          </Typography>
          {locationName !== null && !isPhone ? (
            <Typography noWrap sx={{ fontSize: 12, color: 'text.secondary' }}>
              Help for {locationName}
            </Typography>
          ) : null}
        </Box>
        <IconAction
          label="New chat"
          disabled={!canClear}
          onClick={handleNewChat}
          sx={{ width: 44, height: 44 }}
        >
          <Icon name="edit_square" sx={{ fontSize: 22 }} />
        </IconAction>
        <IconAction
          label="This session's questions"
          aria-pressed={inHistory}
          onClick={() => setView(inHistory ? 'chat' : 'history')}
          sx={{ width: 44, height: 44 }}
        >
          <Icon name="history" sx={{ fontSize: 22 }} />
        </IconAction>
        {!isPhone ? (
          <IconAction
            label={expanded ? 'Shrink Ask Dink' : 'Expand Ask Dink'}
            onClick={() => setExpanded((prev) => !prev)}
            sx={{ width: 44, height: 44 }}
          >
            <Icon name={expanded ? 'close_fullscreen' : 'open_in_full'} sx={{ fontSize: 20 }} />
          </IconAction>
        ) : null}
        <IconAction label="Close Ask Dink" onClick={handleClose} sx={{ width: 44, height: 44 }}>
          <Icon name="close" sx={{ fontSize: 22 }} />
        </IconAction>
      </Stack>

      {inHistory ? (
        <>
          <Stack
            direction="row"
            sx={{
              alignItems: 'center',
              gap: 0.5,
              px: 1,
              py: 0.5,
              borderBottom: 1,
              borderColor: 'divider',
            }}
          >
            <IconAction
              label="Back to the current answer"
              onClick={() => setView('chat')}
              sx={{ width: 44, height: 44 }}
            >
              <Icon name="arrow_back" sx={{ fontSize: 22 }} />
            </IconAction>
            <Typography component="h3" sx={{ fontSize: 15, fontWeight: 700 }}>
              {"This session's questions"}
            </Typography>
          </Stack>
          <Box sx={{ flex: 1, overflowY: 'auto', px: 2, py: 2 }}>
            {log.length === 0 ? (
              <Typography sx={{ fontSize: 14, color: 'text.secondary' }}>
                No questions yet this session.
              </Typography>
            ) : (
              <Stack component="ul" sx={{ gap: 0.5, m: 0, p: 0, listStyle: 'none' }}>
                {[...log].reverse().map((exchange) => {
                  const viewing = thread.includes(exchange.key);
                  const source =
                    exchange.kind === 'help'
                      ? 'Help guide'
                      : exchange.kind === 'unsure'
                        ? 'Not sure'
                        : 'Answer';
                  return (
                    <li key={exchange.key}>
                      <ButtonBase
                        onClick={() => handleOpenPast(exchange.key)}
                        sx={{
                          width: '100%',
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: 1.5,
                          textAlign: 'left',
                          p: 1.25,
                          borderRadius: 2,
                          bgcolor: viewing ? 'action.selected' : 'transparent',
                          '&:hover': { bgcolor: 'action.hover' },
                        }}
                      >
                        <Icon
                          name={HISTORY_ICON[exchange.kind].name}
                          sx={{ fontSize: 20, mt: 0.25, color: HISTORY_ICON[exchange.kind].color }}
                        />
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
                            {exchange.question}
                          </Typography>
                          <Typography sx={{ fontSize: 12.5, color: 'text.secondary' }}>
                            {viewing ? `${source} · Viewing now` : source}
                          </Typography>
                        </Box>
                        <Typography
                          sx={{
                            fontFamily: MONO,
                            fontSize: 12,
                            color: 'text.secondary',
                            flex: 'none',
                          }}
                        >
                          {formatInstantMs(exchange.answeredAt, calendarZone, TIME_ONLY)}
                        </Typography>
                      </ButtonBase>
                    </li>
                  );
                })}
              </Stack>
            )}
          </Box>
          <Stack
            sx={{ gap: 1, p: 2, borderTop: 1, borderColor: 'divider', alignItems: 'flex-start' }}
          >
            <Typography sx={{ fontSize: 12.5, color: 'text.secondary' }}>
              {HISTORY_PRIVACY_COPY}
            </Typography>
            <Button
              variant="outlined"
              startIcon={<Icon name="add" sx={{ fontSize: 18 }} />}
              onClick={handleNewChat}
            >
              Start a new question
            </Button>
          </Stack>
        </>
      ) : (
        <>
          <Typography
            component="p"
            sx={{
              m: 0,
              px: 2,
              py: 1,
              fontSize: 12.5,
              color: 'text.secondary',
              bgcolor: 'action.hover',
              borderBottom: 1,
              borderColor: 'divider',
              '& b': { color: 'text.primary', fontWeight: 600 },
            }}
          >
            {screen.name !== null ? (
              <>
                On <b>{screen.name}</b> ·{' '}
              </>
            ) : null}
            Answers match <b>{role}</b> access
          </Typography>

          <Box sx={{ flex: 1, overflowY: 'auto', px: 2, py: 2 }} aria-live="polite">
            {empty ? (
              <Stack sx={{ gap: 2 }}>
                <Box>
                  <Typography component="h3" sx={{ fontSize: 18, fontWeight: 700 }}>
                    {firstName !== null
                      ? `What's on your mind, ${firstName}?`
                      : "What's on your mind?"}
                  </Typography>
                  <Typography sx={{ fontSize: 14, color: 'text.secondary', mt: 0.5 }}>
                    {
                      "Ask how to do something in Dink. You'll get the steps and a button to the right screen."
                    }
                  </Typography>
                </Box>
                {starters.length > 0 ? (
                  <Stack sx={{ gap: 1 }}>
                    <Typography sx={{ fontSize: 12, fontWeight: 600, color: 'text.secondary' }}>
                      {startersLabel}
                    </Typography>
                    {starters.map((starter) => (
                      <Button
                        key={starter.question}
                        variant="outlined"
                        onClick={() => void ask(starter.question)}
                        sx={{ justifyContent: 'flex-start', textAlign: 'left' }}
                      >
                        {starter.question}
                      </Button>
                    ))}
                  </Stack>
                ) : null}
                <Stack direction={isPhone ? 'column' : 'row'} sx={{ gap: 2 }}>
                  {[
                    { title: 'Dink can', items: DINK_CAN, icon: 'check', color: 'success.main' },
                    {
                      title: "Dink can't",
                      items: DINK_CANT,
                      icon: 'block',
                      color: 'text.secondary',
                    },
                  ].map((group) => (
                    <Box key={group.title} sx={{ flex: 1 }}>
                      <Typography
                        sx={{ fontSize: 12, fontWeight: 600, color: 'text.secondary', mb: 0.5 }}
                      >
                        {group.title}
                      </Typography>
                      <Stack component="ul" sx={{ gap: 0.5, m: 0, p: 0, listStyle: 'none' }}>
                        {group.items.map((item) => (
                          <Stack
                            component="li"
                            key={item}
                            direction="row"
                            sx={{ gap: 1, alignItems: 'center', fontSize: 13 }}
                          >
                            <Icon name={group.icon} sx={{ fontSize: 16, color: group.color }} />
                            {item}
                          </Stack>
                        ))}
                      </Stack>
                    </Box>
                  ))}
                </Stack>
              </Stack>
            ) : null}

            {threadExchanges.map((exchange) => (
              <AskDinkAnswer
                key={exchange.key}
                exchange={exchange}
                allowedPaths={allowedPaths}
                role={role}
                canAsk={pending === null}
                onNavigate={handleNavigate}
                onAsk={(question) => void ask(question)}
                onFeedback={(helpful) => void handleFeedback(exchange, helpful)}
                onCopy={() => void handleCopy(exchange)}
              />
            ))}

            {pending !== null ? (
              <Stack sx={{ gap: 1.5, mb: 3 }}>
                <QuestionBubble text={pending.question} />
                <LoadingProgress elapsed={elapsed} />
              </Stack>
            ) : null}

            {failure !== null ? (
              <Stack sx={{ gap: 1.5 }}>
                <QuestionBubble text={failure.question} />
                {failure.kind === 'rateLimited' ? (
                  <Alert severity="warning">{RATE_LIMITED_COPY}</Alert>
                ) : (
                  <UnavailableCard at={failure.at} onRetry={() => void ask(failure.question)} />
                )}
              </Stack>
            ) : null}
          </Box>

          <Stack sx={{ gap: 1, px: 2, pt: 1.5, pb: 1.75, borderTop: 1, borderColor: 'divider' }}>
            {picked !== null ? (
              <Stack
                direction="row"
                sx={{
                  alignItems: 'center',
                  gap: 0.5,
                  pl: 1.5,
                  pr: 0.5,
                  borderRadius: '16px',
                  bgcolor: 'action.selected',
                  alignSelf: 'flex-start',
                  maxWidth: '100%',
                }}
              >
                <Typography noWrap sx={{ fontSize: 12.5 }}>
                  {`"${picked.label}" ${picked.role}${
                    picked.section === null ? '' : ` in ${picked.section}`
                  }`}
                </Typography>
                <IconAction
                  label={PICK_CHIP_REMOVE_LABEL}
                  onClick={() => setPicked(null)}
                  sx={{ width: 28, height: 28 }}
                >
                  <Icon name="close" sx={{ fontSize: 16 }} />
                </IconAction>
              </Stack>
            ) : null}
            <Box
              component="form"
              onSubmit={handleSubmit}
              sx={{
                display: 'flex',
                gap: 0.5,
                alignItems: 'flex-end',
                border: 1,
                borderColor: 'divider',
                borderRadius: '24px',
                bgcolor: 'action.hover',
                pl: 2,
                pr: 0.5,
                py: 0.5,
                '&:focus-within': { borderColor: 'primary.main' },
              }}
            >
              <InputBase
                inputRef={inputRef}
                placeholder={
                  askAbout.picking
                    ? PICK_MODE_PLACEHOLDER
                    : voice.listening
                      ? VOICE_LISTENING_PLACEHOLDER
                      : 'Ask how to do something'
                }
                value={draft}
                onChange={(event) => setDraft(event.target.value)}
                onKeyDown={handleInputKeyDown}
                multiline
                maxRows={4}
                fullWidth
                slotProps={{
                  input: { 'aria-label': COMPOSER_LABEL, maxLength: ASSISTANT_QUESTION_MAX },
                }}
                sx={{ fontSize: 14, py: 1.1 }}
              />
              {/* Between the box and Send: the mic (DNKADM-0462) renders first, then the
              composerExtras slot that DNKADM-0463 ("Ask about this") fills. */}
              {voice.supported && !voice.denied ? (
                <IconAction
                  label={VOICE_BUTTON_LABEL}
                  aria-pressed={voice.listening}
                  onClick={voice.toggle}
                  color={voice.listening ? 'error' : 'default'}
                  sx={{
                    width: isPhone ? 44 : 40,
                    height: isPhone ? 44 : 40,
                  }}
                >
                  <Icon
                    name="mic"
                    sx={{
                      fontSize: 22,
                      '@keyframes askDinkMicPulse': {
                        '0%, 100%': { opacity: 1 },
                        '50%': { opacity: 0.4 },
                      },
                      animation: voice.listening
                        ? 'askDinkMicPulse 1.2s ease-in-out infinite'
                        : 'none',
                    }}
                  />
                </IconAction>
              ) : null}
              {!isPhone ? (
                <IconAction
                  label={PICK_BUTTON_LABEL}
                  aria-pressed={askAbout.picking}
                  onClick={() => {
                    voice.cancel();
                    askAbout.toggle();
                  }}
                  color={askAbout.picking ? 'primary' : 'default'}
                  sx={{ width: 40, height: 40 }}
                >
                  <Icon name="ads_click" sx={{ fontSize: 22 }} />
                </IconAction>
              ) : null}
              {composerExtras !== undefined ? (
                <Box sx={{ display: 'flex', alignItems: 'center', flex: 'none' }}>
                  {composerExtras}
                </Box>
              ) : null}
              {pending !== null ? (
                <IconAction label="Stop" onClick={handleStop} sx={{ width: 40, height: 40 }}>
                  <Icon name="stop_circle" sx={{ fontSize: 24 }} />
                </IconAction>
              ) : (
                <IconAction
                  label="Send"
                  type="submit"
                  color="primary"
                  disabled={draft.trim() === ''}
                  sx={{ width: 40, height: 40 }}
                >
                  <Icon name="arrow_upward" sx={{ fontSize: 22 }} />
                </IconAction>
              )}
            </Box>
            {voice.denied ? (
              <Typography role="status" sx={{ fontSize: 12.5, color: 'text.secondary' }}>
                {VOICE_BLOCKED_COPY}
              </Typography>
            ) : null}
            <Stack direction="row" sx={{ justifyContent: 'space-between', gap: 1 }}>
              <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>{FOOTER_COPY}</Typography>
              {!isPhone ? (
                <Typography sx={{ fontSize: 12, color: 'text.secondary', flex: 'none' }}>
                  Press{' '}
                  <Box component="kbd" sx={{ fontFamily: MONO }}>
                    ?
                  </Box>{' '}
                  anytime
                </Typography>
              ) : null}
            </Stack>
          </Stack>
        </>
      )}
      {askAbout.overlay}
    </Drawer>
  );
}
